import React, { useState, useEffect } from 'react';
import { Link, NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import { Crown, ChevronRight, ArrowLeft, LogOut, Menu, X, Loader2, Wrench, Pencil, Megaphone } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import LangSwitcher from '@/components/LangSwitcher';
import SuperAdminNotifications from '@/components/SuperAdminNotifications';
import { useSaNotifications } from '@/hooks/useSaNotifications';
import { SA_SECTIONS, SA_GROUPS } from '@/pages/app/superAdminSections';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function MaintenanceToggle() {
    const { session } = useAuth();
    const [active, setActive] = useState(null);
    const [message, setMessage] = useState('');
    const [draft, setDraft] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [notifyOpen, setNotifyOpen] = useState(false);
    const [subStats, setSubStats] = useState(null);
    const [sending, setSending] = useState(false);

    const openNotify = async () => {
        setNotifyOpen(true);
        setSubStats(null);
        try {
            const r = await axios.get(`${API}/tech/maintenance/subscribers`, {
                headers: { Authorization: `Bearer ${session.access_token}` },
            });
            setSubStats(r.data);
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    const sendReturnEmail = async () => {
        setSending(true);
        try {
            const r = await axios.post(`${API}/tech/maintenance/notify-return`, {}, {
                headers: { Authorization: `Bearer ${session.access_token}` },
            });
            toast.success(r.data.sent > 0
                ? `Email « site de retour » envoyé à ${r.data.sent} inscrit(s)`
                : 'Aucun inscrit en attente — tous ont déjà été prévenus');
            setNotifyOpen(false);
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setSending(false);
    };

    useEffect(() => {
        axios.get(`${API}/tech/maintenance`).then(r => {
            setActive(!!r.data.active);
            setMessage(r.data.message || '');
        }).catch(() => setActive(false));
    }, []);

    const save = async (nextActive, nextMessage) => {
        setBusy(true);
        try {
            await axios.put(`${API}/tech/maintenance`, { active: nextActive, message: nextMessage }, {
                headers: { Authorization: `Bearer ${session.access_token}` },
            });
            setActive(nextActive);
            setMessage(nextMessage);
            setDialogOpen(false);
            toast.success(nextActive ? 'Mode maintenance ACTIVÉ — site public masqué' : 'Mode maintenance désactivé — site public rouvert');
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
        setBusy(false);
    };

    const onSwitch = (next) => {
        if (next) {
            setDraft(message);
            setDialogOpen(true);
        } else {
            save(false, message);
        }
    };

    return (
        <>
            <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-colors ${active ? 'bg-red-500/10 border-red-500/40' : 'border-stone-700'}`}
                data-testid="sa-maintenance-toggle">
                <Wrench size={15} strokeWidth={1.6} className={active ? 'text-red-400' : 'text-stone-400'} />
                <div className="flex-1 min-w-0">
                    <div className={`text-sm leading-tight ${active ? 'text-red-300 font-medium' : 'text-stone-300'}`}>Mode maintenance</div>
                    <div className="text-[10px] uppercase tracking-wider text-stone-500">{active === null ? '…' : active ? 'Site public masqué' : 'Site public en ligne'}</div>
                </div>
                {active && (
                    <button type="button" onClick={() => { setDraft(message); setDialogOpen(true); }}
                        className="h-7 w-7 rounded-md flex items-center justify-center text-stone-400 hover:text-white hover:bg-white/10 transition-colors"
                        aria-label="Modifier le message" data-testid="sa-maintenance-edit-btn">
                        <Pencil size={13} strokeWidth={1.6} />
                    </button>
                )}
                {!active && active !== null && (
                    <button type="button" onClick={openNotify}
                        className="h-7 w-7 rounded-md flex items-center justify-center text-stone-400 hover:text-white hover:bg-white/10 transition-colors"
                        aria-label="Prévenir les inscrits du retour" title="Prévenir les inscrits du retour du site"
                        data-testid="sa-maintenance-notify-btn">
                        <Megaphone size={13} strokeWidth={1.6} />
                    </button>
                )}
                <Switch checked={!!active} disabled={busy || active === null} onCheckedChange={onSwitch} data-testid="sa-maintenance-switch" />
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-md" data-testid="sa-maintenance-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading flex items-center gap-2">
                            <Wrench size={17} strokeWidth={1.7} /> {active ? 'Message de maintenance' : 'Activer le mode maintenance'}
                        </DialogTitle>
                        <DialogDescription>
                            Le site public sera masqué pour les visiteurs. Les comptes actifs pourront toujours se connecter.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Message personnalisé (optionnel)</label>
                        <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3}
                            placeholder="Ex. : Retour prévu le 15/07 à 18h. Merci de votre patience."
                            data-testid="sa-maintenance-message-input" />
                        <p className="text-xs text-muted-foreground">Laissez vide pour afficher le message par défaut.</p>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="sa-maintenance-cancel-btn">Annuler</Button>
                        <Button onClick={() => save(true, draft)} disabled={busy}
                            className="bg-red-600 hover:bg-red-700 text-white" data-testid="sa-maintenance-confirm-btn">
                            {active ? 'Enregistrer le message' : 'Activer la maintenance'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog open={notifyOpen} onOpenChange={setNotifyOpen}>
                <DialogContent className="sm:max-w-md" data-testid="sa-maintenance-notify-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading flex items-center gap-2">
                            <Megaphone size={17} strokeWidth={1.7} /> Annoncer le retour du site
                        </DialogTitle>
                        <DialogDescription>
                            Envoie l'email « le site est de retour en ligne » aux personnes inscrites depuis la page de maintenance.
                            Les inscrits déjà prévenus ne recevront pas de doublon.
                        </DialogDescription>
                    </DialogHeader>
                    {subStats === null ? (
                        <div className="flex justify-center py-4"><Loader2 className="animate-spin" size={18} /></div>
                    ) : (
                        <div className="grid grid-cols-3 gap-3 text-center text-sm">
                            <div className="rounded-lg border border-border p-3">
                                <div className="font-heading text-xl">{subStats.total}</div>
                                <div className="text-xs text-muted-foreground">Inscrits</div>
                            </div>
                            <div className="rounded-lg border border-border p-3">
                                <div className="font-heading text-xl text-amber-600">{subStats.pending}</div>
                                <div className="text-xs text-muted-foreground">En attente</div>
                            </div>
                            <div className="rounded-lg border border-border p-3">
                                <div className="font-heading text-xl text-emerald-600">{subStats.notified}</div>
                                <div className="text-xs text-muted-foreground">Déjà prévenus</div>
                            </div>
                        </div>
                    )}
                    {subStats?.last_sent_at && (
                        <p className="text-xs text-muted-foreground">Dernier envoi : {new Date(subStats.last_sent_at).toLocaleString('fr-FR')}</p>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setNotifyOpen(false)}>Fermer</Button>
                        <Button onClick={sendReturnEmail} disabled={sending || !subStats || subStats.pending === 0}
                            className="bg-primary hover:bg-primary-hover" data-testid="sa-maintenance-notify-send-btn">
                            {sending ? <Loader2 size={14} className="animate-spin mr-2" /> : <Megaphone size={14} className="mr-2" />}
                            Envoyer ({subStats?.pending ?? 0})
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function SidebarGroup({ group, activeSection, onNavigate }) {
    const isActiveGroup = group.sections.includes(activeSection);
    const [open, setOpen] = useState(true);
    useEffect(() => { if (isActiveGroup) setOpen(true); }, [isActiveGroup]);

    return (
        <div className="mb-1" data-testid={`sa-group-${group.key}`}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                aria-expanded={open}
                data-testid={`sa-group-toggle-${group.key}`}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[11px] uppercase tracking-[0.18em] font-medium text-stone-400 hover:text-stone-200 transition-colors"
            >
                <span className="flex-1 text-left">{group.label}</span>
                <ChevronRight size={13} strokeWidth={2} className={`transition-transform duration-300 ${open ? 'rotate-90' : ''}`} />
            </button>
            <div className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="space-y-0.5 pb-2">
                    {group.sections.map((key) => {
                        const { label, icon: Icon } = SA_SECTIONS[key];
                        return (
                            <NavLink
                                key={key}
                                to={`/app/super-admin/${key}`}
                                onClick={onNavigate}
                                data-testid={`sa-nav-${key}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${isActive
                                        ? 'bg-primary/20 text-amber-300 font-medium'
                                        : 'text-stone-300 hover:bg-white/5 hover:text-white'
                                    }`
                                }
                            >
                                <Icon size={16} strokeWidth={1.5} /> {label}
                            </NavLink>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default function SuperAdminLayout() {
    const { profile, signOut } = useAuth();
    const navigate = useNavigate();
    const { section } = useParams();
    const [open, setOpen] = useState(false);
    const activeSection = section || 'overview';
    const { items: notifItems, unread: notifUnread, markAllRead } = useSaNotifications(!!profile?.is_super_admin);

    useEffect(() => {
        if (profile !== null && !profile?.is_super_admin) navigate('/app/dashboard', { replace: true });
    }, [profile, navigate]);

    if (profile === null) {
        return <div className="min-h-screen flex items-center justify-center bg-background" data-testid="sa-layout-loading"><Loader2 className="animate-spin" /></div>;
    }
    if (!profile?.is_super_admin) return null;

    const initials = (profile?.full_name || profile?.email || '?')
        .split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase();

    return (
        <div className="min-h-screen flex bg-background">
            {/* Sidebar — environnement dédié (thème sombre) */}
            <aside
                className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-72 bg-stone-950 border-r border-stone-800 transition-transform ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
                data-testid="sa-sidebar"
            >
                <div className="h-full flex flex-col">
                    <div className="px-6 py-6 border-b border-stone-800">
                        <Link to="/app/super-admin" className="flex items-center gap-3" data-testid="sa-brand-logo">
                            <img src="/brand/african-valley-logo-256.png" alt="African Valley" className="h-10 w-10 object-contain rounded-md shrink-0" draggable={false} />
                            <div>
                                <div className="flex items-baseline gap-1.5">
                                    <span className="font-heading text-lg font-semibold tracking-tight text-white">African</span>
                                    <span className="font-heading text-lg font-medium text-primary">Valley</span>
                                </div>
                                <div className="text-[9px] uppercase tracking-[0.2em] text-stone-400 mt-0.5 font-body">Projet 360°</div>
                            </div>
                        </Link>
                        <div className="mt-4 flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-gradient-to-r from-amber-500/15 to-amber-600/5 border border-amber-500/25" data-testid="sa-env-badge">
                            <Crown size={16} strokeWidth={1.8} className="text-amber-400" />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-amber-300 leading-tight">Super Admin</div>
                                <div className="text-[10px] uppercase tracking-wider text-stone-400">Environnement dédié</div>
                            </div>
                            <SuperAdminNotifications items={notifItems} unread={notifUnread} markAllRead={markAllRead} />
                        </div>
                    </div>

                    <nav className="flex-1 px-3 py-4 overflow-y-auto" data-testid="sa-nav">
                        {SA_GROUPS.map(group => (
                            <SidebarGroup key={group.key} group={group} activeSection={activeSection} onNavigate={() => setOpen(false)} />
                        ))}
                    </nav>

                    <div className="border-t border-stone-800 p-4 space-y-3">
                        <MaintenanceToggle />
                        <Link
                            to="/app/dashboard"
                            data-testid="sa-back-to-app"
                            className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-stone-300 border border-stone-700 hover:bg-white/5 hover:text-white transition-colors"
                        >
                            <ArrowLeft size={15} strokeWidth={1.6} /> Retour à l'espace client
                        </Link>
                        <div className="flex items-center justify-between gap-2">
                            <LangSwitcher />
                            <button
                                type="button"
                                onClick={async () => { await signOut(); navigate('/'); }}
                                data-testid="sa-signout-btn"
                                className="h-9 w-9 rounded-md flex items-center justify-center text-stone-400 hover:text-white hover:bg-white/5 transition-colors"
                                aria-label="Déconnexion"
                            >
                                <LogOut size={16} strokeWidth={1.6} />
                            </button>
                        </div>
                        <div className="flex items-center gap-3 px-1">
                            <div className="h-9 w-9 rounded-full bg-amber-500/20 text-amber-300 flex items-center justify-center text-sm font-medium">{initials}</div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-white truncate">{profile?.full_name || profile?.email}</div>
                                <div className="text-[11px] text-stone-400 truncate">{profile?.email}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Mobile toggle */}
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="lg:hidden fixed top-4 left-4 z-50 h-10 w-10 rounded-md bg-stone-900 text-white border border-stone-700 flex items-center justify-center shadow-soft"
                aria-label="Toggle menu"
                data-testid="sa-mobile-menu-toggle"
            >
                {open ? <X size={18} /> : <Menu size={18} />}
            </button>

            <main className="flex-1 min-w-0">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 lg:py-12">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
